/**
 * @file 피드백 게시판 API
 * 사내 피드백 목록/작성/수정/삭제/상태변경 및 댓글을 처리합니다.
 *
 * 익명 정책: 서버가 관리자(GLOBAL_ADMIN) 외의 열람자에게는 응답에서 작성자(author)를 비워서 내려줍니다.
 * 본인 글/댓글 여부는 응답의 `mine` 플래그로 판단합니다 (author.id 로 판단하지 않음).
 */
import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API_BASE}/feedback`;

export const feedbackApi = {
    /** 목록 (최신순, 페이지네이션). params: { status?, page, size } */
    list: (params = {}) => api.get(BASE, params),

    /** 단건 상세 (댓글 포함) */
    get: (id) => api.get(`${BASE}/${id}`),

    /** 작성 — body: { title?, content } */
    create: (data) => api.post(BASE, data),

    /** 수정 (작성자 본인) — body: { title?, content } */
    update: (id, data) => api.put(`${BASE}/${id}`, data),

    /** 삭제 (작성자 본인 또는 관리자) */
    remove: (id) => api.delete(`${BASE}/${id}`),

    /** 상태 변경 (REVIEW | IN_PROGRESS | DONE) — 관리자(GLOBAL_ADMIN) 전용 */
    updateStatus: (id, status) => api.patch(`${BASE}/${id}/status`, { status }),

    /** 좋아요 토글 — { liked, likeCount } 반환 */
    toggleLike: (id) => api.post(`${BASE}/${id}/like`),

    // ── 댓글 ──────────────────────────────────
    /** 댓글 작성 — body: { content } */
    createComment: (id, data) => api.post(`${BASE}/${id}/comments`, data),

    /** 댓글 수정 (작성자 본인) */
    updateComment: (commentId, data) => api.put(`${BASE}/comments/${commentId}`, data),

    /** 댓글 삭제 (작성자 본인 또는 관리자) */
    deleteComment: (commentId) => api.delete(`${BASE}/comments/${commentId}`),

    // ── 첨부파일 (이미지/파일) ──────────────────
    /** 첨부 업로드 (multipart) — 작성자 본인만. 작성 직후 호출. */
    uploadAttachment: (feedbackId, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${BASE}/${feedbackId}/attachments`, form);
    },

    /** 첨부 삭제 (업로더 본인 또는 관리자) */
    deleteAttachment: (attachmentId) => api.delete(`${BASE}/attachments/${attachmentId}`),

    /** 첨부 미리보기 (blob). PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환. */
    previewAttachment: (attachmentId) => api.download(`${BASE}/attachments/${attachmentId}/preview`),
};
